import instantsearch from 'instantsearch.js/dist/instantsearch.production.min';
import {
    searchBox,
    hits,
    configure,
    refinementList,
    menuSelect,
    currentRefinements,
    clearRefinements,
    pagination,
    hitsPerPage,
    stats,
    index
} from 'instantsearch.js/es/widgets';
import {connectPagination, connectSearchBox} from 'instantsearch.js/es/connectors';
// import { helper } from 'instantsearch.js/es/helpers'

import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";

/*
 * Search architecture on this page:
 * 1) `mainSearch` uses a Typesense adapter with `union: true` and combines
 *    Andover + Athletics into one merged result stream for the main results UI.
 * 2) `eventsSearch` uses a separate adapter with `union: true` and combines
 *    Andover events + Athletics events into one merged event result stream
 *    rendered into #event-hits / #event-stats / #event-pagination.
 * 3) Both search instances share the same input:
 *    - mainSearch owns the searchBox widget
 *    - eventsSearch query is synchronized from mainSearch UI state and also
 *      triggered directly on Enter/reset/initial URL query.
 */
/*
 * Maintenance mental model:
 * - This file runs two separate InstantSearch instances (`mainSearch`, `eventsSearch`).
 * - `mainSearch` owns the visible input, but `eventsSearch` needs the same query to stay in sync.
 * - InstantSearch widgets can re-render and replace DOM nodes (for example reset button markup),
 *   so direct one-time element listeners can become stale.
 * - Prefer deriving UI from query state during render (single source of truth) instead of toggling
 *   visibility in many unrelated handlers.
 * - When wiring listeners to widget-owned elements, use defensive null checks or delegated listeners.
 */

const SEARCH_ONLY = import.meta.env.VITE_TYPESENSE_SEARCH_ONLY_API_KEY || '';
const SEARCH_HOST = import.meta.env.VITE_TYPESENSE_HOST || '';
const SEARCH_PORT = import.meta.env.VITE_TYPESENSE_PORT || '';
const SEARCH_PROTOCOL = import.meta.env.VITE_TYPESENSE_PROTOCOL || '';
const indexName = import.meta.env.VITE_SEARCH_INDEX || '';
const eventIndexName = import.meta.env.VITE_SEARCH_EVENTS_INDEX || '';
const athleticsIndexName = import.meta.env.VITE_SEARCH_ATHLETICS_INDEX || '';
const eventAthleticsIndexName = import.meta.env.VITE_SEARCH_ATHLETICS_EVENTS_INDEX || '';
const params = new URLSearchParams(window.location.search);
console.log(params);
const getInitialQueryFromParams = (searchParams) => {
    const q = searchParams.get('q');
    console.log(q);
    if (typeof q === 'string' && q.trim() !== '') return q.trim();

    for (const [key, value] of searchParams.entries()) {
        if (key.endsWith('[query]') && typeof value === 'string' && value.trim() !== '') {
            return value.trim();
        }
    }

    for (const [, value] of searchParams.entries()) {
        if (typeof value === 'string' && value.trim() !== '') {
            return value.trim();
        }
    }

    return '';
};
const initialQuery = getInitialQueryFromParams(params);
const getInitialPageFromParams = (searchParams, key) => {
    const raw = searchParams.get(key);
    const parsed = Number.parseInt(raw ?? '', 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
};
const initialMainPage = getInitialPageFromParams(params, 'p');
const initialEventsPage = getInitialPageFromParams(params, 'ep');
const refreshAddToCalendar = () => {
    if (window.addeventatc && typeof window.addeventatc.refresh === 'function') {
        window.addeventatc.refresh();
    }
};

const now = new Date();
const utcTimestamp = Math.floor(now.getTime() / 1000); // Adjust for UTC if needed

// const virtualSearchBox = connectSearchBox(() => null);
// console.log(import.meta.env);
// console.log(SEARCH_ONLY);
// console.log(SEARCH_HOST);
// console.log(indexName);
// console.log(eventIndexName);

// Create the render function
const renderPagination = (renderOptions, isFirstRender) => {
    const {
        pages,
        currentRefinement,
        nbPages,
        isFirstPage,
        isLastPage,
        refine,
        createURL,
        widgetParams
    } = renderOptions;

    const { container } = widgetParams;

    if (nbPages <= 1) {
        container.style.display = 'none';
        return;
    } else {
        container.style.display = 'block';
    }

    const first = `<svg class="svg-inline--fa" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Pro 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2025 Fonticons, Inc.--><path d="M47 239c-9.4 9.4-9.4 24.6 0 33.9L239 465c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9L97.9 256 273 81c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0L47 239zM431 47L239 239c-9.4 9.4-9.4 24.6 0 33.9L431 465c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-175-175L465 81c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0z"/></svg>`;
    const previous = `<svg class="svg-inline--fa" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--!Font Awesome Pro 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2025 Fonticons, Inc.--><path d="M15 239c-9.4 9.4-9.4 24.6 0 33.9L207 465c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9L65.9 256 241 81c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0L15 239z"/></svg>`;
    const next = `<svg class="svg-inline--fa" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--!Font Awesome Pro 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2025 Fonticons, Inc.--><path d="M305 239c9.4 9.4 9.4 24.6 0 33.9L113 465c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l175-175L79 81c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0L305 239z"/></svg>`;
    const last = `<svg class="svg-inline--fa" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Pro 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2025 Fonticons, Inc.--><path d="M465 239c9.4 9.4 9.4 24.6 0 33.9L273 465c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l175-175L239 81c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0L465 239zM81 47L273 239c9.4 9.4 9.4 24.6 0 33.9L81 465c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l175-175L47 81c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0z"/></svg>`;
    widgetParams.container.innerHTML = `
    <div class="query__pagination">
        <ul class="page-nav">
          ${
            !isFirstPage
                ? `
                <li>
                  <a
                    href="${createURL(0)}"
                    data-value="${0}"
                  >
                    ${first}
                  </a>
                </li>
                <li>
                  <a
                    href="${createURL(currentRefinement - 1)}"
                    data-value="${currentRefinement - 1}"
                  >
                    ${previous}
                  </a>
                </li>
                `
                : ''
        }
          ${pages
            .map(
                page => `
                <li ${currentRefinement === page ? 'class="current-page"' : ''}>
                  <a
                    href="${createURL(page)}"
                    data-value="${page}"
                  >
                    ${page + 1}
                  </a>
                </li>
              `
            )
            .join('')}
            ${
            !isLastPage
                ? `
                  <li>
                    <a
                      href="${createURL(currentRefinement + 1)}"
                      data-value="${currentRefinement + 1}"
                    >
                      ${next}
                    </a>
                  </li>
                  <li>
                    <a
                      href="${createURL(nbPages - 1)}"
                      data-value="${nbPages - 1}"
                    >
                      ${last}
                    </a>
                  </li>
                  `
                : ''
        }
        </ul>
    </div>
  `;

    [...container.querySelectorAll('a')].forEach(element => {
        element.addEventListener('click', event => {
            setTimeout(refreshAddToCalendar, 200);
            event.preventDefault();
            refine(event.currentTarget.dataset.value);
        });
    });
};

// Create the custom widget
const customPagination = connectPagination(
    renderPagination
);
const virtualSearchBox = connectSearchBox(() => null);






const mainTypesenseAdapter = new TypesenseInstantSearchAdapter({
    server: {
        apiKey: SEARCH_ONLY, // Be sure to use the search-only-api-key
        nodes: [
            {
                host: SEARCH_HOST,
                port: SEARCH_PORT,
                protocol: SEARCH_PROTOCOL
            }
        ]
    },
    union: true,
    // The following parameters are directly passed to Typesense's search API endpoint.
    //  So you can pass any parameters supported by the search endpoint below.
    //  query_by is required.
    collectionSpecificSearchParameters: {
        [indexName]: {
            query_by: 'prioritizedSearchTerms,title,headline,summary,shortBio,personTitle',
            sort_by: '_text_match:desc,title:asc'
        },
        [athleticsIndexName]: {
            query_by: 'prioritizedSearchTerms,sport,title,summary,shortBio,personTitle,playersList.name,handle,season',
            sort_by: '_text_match:desc,title:asc',
            highlight_full_fields: 'summary',
        },
    },
});
const eventsTypesenseAdapter = new TypesenseInstantSearchAdapter({
    server: {
        apiKey: SEARCH_ONLY,
        nodes: [
            {
                host: SEARCH_HOST,
                port: SEARCH_PORT,
                protocol: SEARCH_PROTOCOL
            }
        ]
    },
    union: true,
    collectionSpecificSearchParameters: {
        [eventIndexName]: {
            query_by: 'title,content',
            sort_by: 'start_date_timestamp:asc,_text_match:desc,title:asc',
            filter_by: `end_date_timestamp:>=${utcTimestamp}`,
        },
        [eventAthleticsIndexName]: {
            query_by: 'sport,title,opponent',
            filter_by: `end_date_timestamp:>=${utcTimestamp}`,
            sort_by: 'start_date_timestamp:asc,_text_match:desc,title:asc',
            drop_tokens_threshold: 0,
            typo_tokens_threshold:0,
            num_typos:'0,2,2',
        },
    },
});
const mainTypesenseClient = mainTypesenseAdapter.searchClient;
const eventsTypesenseClient = eventsTypesenseAdapter.searchClient;

const createSearchClient = (typesenseClient, label) => ({
    ...typesenseClient,
    search(requests) {
        const requestId = Date.now(); // Unique ID for this batch
        console.log(`[${label}] Search requests [${requestId}]:`, requests.map(req => ({
            indexName: req.indexName,
            query: req.params.query,
            page: req.params.page,
            results: req.nbHits
        })));
        if(requests.every(({ params }) => !params.query)) {
            return Promise.resolve({
                results: requests.map(() => ({
                    hits: [],
                    nbHits: 0,
                    nbPages: 0,
                    page: 0,
                    processingTimeMS: 0,
                    hitsPerPage: 0,
                    exhaustiveNbHits: false,
                    query: '',
                    params: '',
                })),
            });
        }

        return typesenseClient.search(requests);
    },
});
const mainSearchClient = createSearchClient(mainTypesenseClient, 'main');
const eventsSearchClient = createSearchClient(eventsTypesenseClient, 'events');
const renderWidget = {
    render({instantSearchInstance}) {
        const state = instantSearchInstance.renderState[indexName];
        const query = state?.searchBox?.query?.trim();

        const wrapper = document.getElementById('results-container');

        if (wrapper) {
            wrapper.style.display = query ? 'block' : 'none';
        }
    }
};
const eventsRenderWidget = {
    render({instantSearchInstance}) {
        const state = instantSearchInstance.renderState[eventIndexName];
        const results = state?.stats?.nbHits;
        const wrapper = document.getElementById('event-results-container');
        if (wrapper) {
            wrapper.style.display = results ? 'block' : 'none';
        }
        console.log(state);
        console.log(state?.stats?.nbHits)
        // Event hits are rendered dynamically; refresh AddEvent buttons after each render.
        setTimeout(refreshAddToCalendar, 0);
    }
};

const mainSearch = instantsearch({
    searchClient: mainSearchClient,
    indexName: indexName,
    routing: false,
    initialUiState: initialQuery ? {
        [indexName]: { query: initialQuery, page: initialMainPage },
        [athleticsIndexName]: { query: initialQuery, page: initialMainPage },
    } : undefined,
    future: {
        preserveSharedStateOnUnmount: true,
    },
});
const eventsSearch = instantsearch({
    searchClient: eventsSearchClient,
    indexName: eventIndexName,
    initialUiState: initialQuery ? {
        [eventIndexName]: { query: initialQuery, page: initialEventsPage },
        [eventAthleticsIndexName]: { query: initialQuery, page: initialEventsPage },
    } : undefined,
    future: {
        preserveSharedStateOnUnmount: true,
    },
});

mainSearch.addWidgets([
    searchBox({
        container: '#searchbox',
        placeholder: 'Search',
        searchAsYouType: false,
        showSubmit: false,
        cssClasses: {
            root: 'results__searchbar',
            form: 'results__search',
            input: 'results__input'
        },
    }),

    hitsPerPage({
        container: '#hits-per-page',
        items: [
            {label: 'Limit: 10', value: 10, default: true},
            {label: 'Limit: 20', value: 20},
            {label: 'Limit: 50', value: 50},
        ],
        cssClasses: {
            select: [
                'results__filter',
            ],
        },
    }),
    hits({
        container: '#hits',
        templates: {
            item(hit, {html, components}) {

                const externalImgCredit =
                    hit.externalImgCredit
                        ? html`<p class="news__photo-credit">${hit.externalImgCredit}</p>`
                        : '';
                const newsSourceLogo =
                    hit.newsSourceLogoSrc
                        ? html`<img src="${hit.newsSourceLogoSrc}" alt="caption goes here"
                                    class="external-source__img"/>`
                        : '';
                const nsTitle =
                    hit.newsSourceTitle
                        ? html`
                            <div class="news__external-source"><p
                                class="external-source__title${hit.newsSourceLogoSrc ? html` visually-hidden` : ''}">
                                ${hit.newsSourceTitle}</p></div>`
                        : '';
                const newsSource =
                    hit.newsSourceUrl
                        ? html`<a href="${hit.newsSourceUrl}" target="_blank"
                                  class="news__external-source">${newsSourceLogo}${nsTitle}</a>`
                        : html`${hit.newsSourceTitle ? nsTitle : ''}`;
                const imageSrc =
                    hit.imageSrc
                        ? html`<a href="${hit.externalUrl ? hit.externalUrl : hit.siteUrl + hit.uri}"
                                  class="results__image ${hit.handle === 'news' ? 'news-featured__image' : 'results__image-image'}"><img
                            src="${hit.imageSrc}" srcset="${hit.imageSrcset}" alt="${hit.imageAltText}"
                            class="${hit.handle === 'news' ? 'news-featured__image-image' : 'results__image-image'}"
                            sizes="(min-width:641px) 30vw, 100vw"/></a>`
                        : '';
                const previewImgSrc =
                    hit.previewImgSrc
                        ? html`<a href="${hit.externalUrl ? hit.externalUrl : hit.siteUrl + hit.uri}"
                                  class="results__image ${hit.handle === 'news' ? 'news-featured__image' : 'results__image-image'}"><img
                            src="${hit.previewImgSrc}" srcset="${hit.previewImgSrcset}" alt="${hit.previewImgAltText}"
                            class="${hit.handle === 'news' ? 'news-featured__image-image' : 'results__image-image'}"
                            sizes="(min-width:641px) 30vw, 100vw"/></a>`
                        : '';
                const externalImgSrc =
                    hit.externalImgSrc
                        ? html`<a href="${hit.externalUrl ? hit.externalUrl : hit.siteUrl + hit.uri}"
                                  class="results__image ${hit.handle === 'news' ? 'news-featured__image' : 'results__image-image'}"><img
                            src="${hit.externalImgSrc}" srcset="${hit.externalImgSrcset}" alt="${hit.externalImgAltText}"
                            class="${hit.handle === 'news' ? 'news-featured__image-image' : 'results__image-image'}"
                            sizes="(min-width:641px) 30vw, 100vw"/>${externalImgCredit}</a>`
                        : '';
                const headshotImgSrc =
                    hit.headshotImgSrc
                        ? hit.showLink === true ? html`<a href="${hit.externalUrl ? hit.externalUrl : hit.siteUrl + hit.uri}"><img
                            src="${hit.headshotImgSrc}" srcset="${hit.headshotImgSrcset}" alt="${hit.headshotAltTxt}"
                            class="people__image" sizes="(min-width:641px) 30vw, 100vw"/></a>` : html`
                            <div><img src="${hit.headshotImgSrc}" srcset="${hit.headshotImgSrcset}"
                                      alt="${hit.headshotAltTxt}" class="people__image"
                                      sizes="(min-width:641px) 30vw, 100vw"/></div>`
                        : '';
                const parents = hit.parents !== ''
                    ? html`${hit.parents?.map((parent, i) => html`${i > 0 ? html`<span class="results__sep"> > </span>` : ''} <a href="${hit.siteUrl + parent.uri}" class="results__breadcrumbs-link">${parent.title}</a> `)}`
                    : '';
                const siteName = hit.siteName !== ''
                    ? html`<a href="${hit.siteUrl}" class="results__breadcrumbs-link">${hit.siteName}</a>`
                    : '';
                const breadcrumbs = hit.siteName !== ''
                    ? html`<p class="results__breadcrumbs">${siteName} ${(hit.parents?.length ?? 0) > 0 ? html`<span class="results__sep"> > </span>` : ''} ${parents}</p>`
                    : '';
                const peopleBlock =
                    hit.handle === 'people'
                        ? html`
                            <div class="news__article-content">
                                ${headshotImgSrc ? headshotImgSrc : ''}
                                <div class="results__right">
                                    ${hit.showLink === true ? html`<a href="${hit.siteUrl + hit.uri}"
                                                                      class="results__entry-title">${components.Highlight({
                                        attribute: 'title',
                                        hit
                                    })}</a>` : html`<h3 class="results__entry-title">
                                        ${components.Highlight({attribute: 'title', hit})}</h3>`}
                                    ${breadcrumbs ? breadcrumbs : ''}
                                    ${hit.shortBio !== '' ? html`<p class="results__description">${components.Highlight({
                                        attribute: 'shortBio',
                                        hit
                                    })}</p>` : hit.personTitle !== '' ? html`<p class="results__description">
                                        ${components.Highlight({attribute: 'personTitle', hit})}</p>` : ''}
                                </div>
                            </div>`
                        : '';
                const contentBlock =
                    html`
                        ${newsSource}
                        <div class="news__article-content">

                            ${imageSrc ? imageSrc : previewImgSrc ? previewImgSrc : externalImgSrc ? externalImgSrc : headshotImgSrc ? headshotImgSrc : ''}

                            <div class="results__right">
                                <a href="${hit.externalUrl ? hit.externalUrl : hit.siteUrl + hit.uri}" class="results__entry-title">
                                    ${components.Highlight({attribute: 'title', hit})}
                                    ${hit.externalUrl || hit.externalLinkLS === true ? html`<i
                                        class="fa-regular fa-arrow-up-right-from-square external-link"></i>` : ''}
                                </a>
                                ${breadcrumbs ? breadcrumbs : ''}
                                ${hit.summary !== ''
                                    ? html`<p class="results__description">
                                        ${components.Highlight({attribute: 'summary', hit})}</p>`
                                    : ''}
                                ${hit.handle === 'news'
                                    ? html`
                                        <div class="news__article-categories">
                                            ${hit.newsCategory?.map((cat) => html`<p class="results__article-category__no-link">${cat}</p> `)}
                                        </div>`
                                    : ''
                                }
                            </div>
                        </div>
                    `;

                return html`
                    ${peopleBlock ? peopleBlock : contentBlock}
                `;
            },
            empty(results, {html}) {
                if (results.query === '') return null;
                return html`<h4>No results found for "${results.query}"</h4>`;
            },
        }
    }),
    pagination({
        container: '#pagination',
        cssClasses: {
            root: 'query__pagination',
            list: 'page-nav',
            selectedItem: 'current-page',
        },
        // templates: {
        //     first() {
        //         return `<svg class="svg-inline--fa" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Pro 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2025 Fonticons, Inc.--><path d="M47 239c-9.4 9.4-9.4 24.6 0 33.9L239 465c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9L97.9 256 273 81c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0L47 239zM431 47L239 239c-9.4 9.4-9.4 24.6 0 33.9L431 465c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-175-175L465 81c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0z"/></svg>`
        //     },
        //     previous() {
        //         return `<svg class="svg-inline--fa" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--!Font Awesome Pro 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2025 Fonticons, Inc.--><path d="M15 239c-9.4 9.4-9.4 24.6 0 33.9L207 465c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9L65.9 256 241 81c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0L15 239z"/></svg>`
        //     },
        //     next() {
        //         return `<svg class="svg-inline--fa" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--!Font Awesome Pro 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2025 Fonticons, Inc.--><path d="M305 239c9.4 9.4 9.4 24.6 0 33.9L113 465c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l175-175L79 81c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0L305 239z"/></svg>`
        //     },
        //     last() {
        //         return `<svg class="svg-inline--fa" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Pro 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2025 Fonticons, Inc.--><path d="M465 239c9.4 9.4 9.4 24.6 0 33.9L273 465c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l175-175L239 81c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0L465 239zM81 47L273 239c9.4 9.4 9.4 24.6 0 33.9L81 465c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l175-175L47 81c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0z"/></svg>`
        //     },
        //
        // },
    }),
    customPagination({
        container: document.querySelector('#pagination'),
    }),
    stats({
        container: '#stats',
        templates: {
            text: ({
                       nbHits,
                       hasNoResults,
                       hasOneResult,
                       processingTimeMS,
                       query,
                   }) => {
                let statsText = '';
                if (hasNoResults) {
                    statsText = 'no results';
                } else if (hasOneResult) {
                    statsText = '1 result found';
                } else {
                    statsText = `${nbHits.toLocaleString()} results`;
                }
                return query !== ''
                    ? `Found ${statsText}.`
                    : ' ';
            },
        },
        cssClasses: {
            root: 'results__stats',
        },
    }),
    configure({
        hitsPerPage: 10
    }),
    renderWidget,
    index({indexName: athleticsIndexName}).addWidgets([
        // Keep the athletics index participating in InstantSearch requests so union can merge results.
        configure({})
    ]),
    // index({indexName: eventIndexName}).addWidgets([
    //     configure({
    //         numericFilters: [`end_date_timestamp >= ${utcTimestamp}`],
    //         hitsPerPage: 5,
    //     }),
    //     hits({
    //         container: '#event-hits',
    //         templates: {
    //             item(hit, {html, components}) {
    //
    //                 const eventDate = new Date(hit.start_date_timestamp * 1000);
    //                 let now = Date.now();
    //                 let isYGreater = formatUTCDateY(hit.start_date_timestamp, "UTC") > formatUTCDateNOWY(now, "UTC");
    //                 // const date = hit.start_date_timestamp * 1000;
    //
    //                 // console.log(formatUTCDate(hit.start_date_timestamp, "UTC")); // "Sun, June 8 at 12:00 AM"
    //                 // console.log(formatDateWithMMDD(hit.start_date_timestamp, "UTC")); // "06.08"
    //                 // console.log(formatDateWithMMDD(hit.end_date_timestamp, "UTC")); // "06.08"
    //                 // console.log(formatDateWithDay(hit.start_date_timestamp, "UTC")); // "Sun"
    //                 // console.log(formatUTCDateYmd(hit.start_date_timestamp, "UTC")); // "Y-m-d"
    //                 // console.log(formatDateWithMM(hit.start_date_timestamp, "UTC")); // "MM"
    //                 // console.log(hit.allDay);
    //                 //
    //                 //
    //                 // console.log(formatDateWithMM(hit.start_date_timestamp, "UTC") !== formatDateWithMM(hit.end_date_timestamp, "UTC"));
    //                 // console.log(formatDateWithMM(hit.start_date_timestamp, "UTC"));
    //                 // console.log(formatDateWithMM(hit.end_date_timestamp, "UTC"));
    //                 // console.log(formatDateWithMM(hit.start_date_timestamp, "UTC") === formatDateWithMM(hit.end_date_timestamp, "UTC"));
    //                 // console.log(formatUTCDateTime(hit.start_date_timestamp, "UTC").replace(/\s+[AP]M/i, ''));
    //                 // console.log(formatUTCDateTime(hit.end_date_timestamp, "UTC"));
    //                 // console.log(eventDate > now);
    //                 // console.log(formatUTCDateY(hit.start_date_timestamp, "UTC") > formatUTCDateNOWY(now, "UTC"));
    //                 console.log(hit.allDay);
    //                 console.log(hit.multiDay);
    //                 const multiDayWk =
    //                     formatDateWithMM(hit.start_date_timestamp, "UTC") === formatDateWithMM(hit.end_date_timestamp, "UTC")
    //                         ? html`<span
    //                             class="calendar__row-week">${formatDateWithDay(hit.start_date_timestamp, "UTC")}
    //                             –${formatDateWithDay(hit.end_date_timestamp, "UTC")}</span>`
    //                         : '';
    //                 const multiDayRow = html`<span
    //                             class="calendar__row-date">${formatDateWithMMDD(hit.start_date_timestamp, "UTC")}–
    //                             ${formatDateWithMM(hit.start_date_timestamp, "UTC") !== formatDateWithMM(hit.end_date_timestamp, "UTC") ? formatDateWithMMDD(hit.end_date_timestamp, "UTC") : formatDateWithDD(hit.end_date_timestamp, "UTC")}</span>`;
    //
    //                 const rowDate = html`<span class="calendar__row-date">${formatDateWithMMDD(hit.start_date_timestamp, "UTC")}</span>`;
    //                 const rowWk = html`<span class="calendar__row-week">${formatDateWithDay(hit.start_date_timestamp, "UTC")}</span>`;
    //                 const rowY = html`<p class="calendar__event__meta-year">${formatUTCDateY(hit.start_date_timestamp, "UTC")}</p>`;
    //
    //                 const timeDuration = html`<p class="calendar__event__meta-time">
    //                     ${formatUTCDateTime(hit.start_date_timestamp, "UTC").replace(/\s+[AP]M/i, '')}
    //                         –${formatUTCDateTime(hit.end_date_timestamp, "UTC")}</p>`;
    //
    //                 const canceled = hit.isCanceled === true ? html`CANCELLED: ` : '';
    //
    //                 const image = html`<a class="calendar__event-hero-img"
    //                                       href="${hit.siteUrl + hit.uri || '#'}" ${hit.externalUrl === true ? html`target="_blank"` : ''}>
    //                     <img src="${hit.imageSrc}"
    //                          srcset="${hit.imageSrcset}"
    //                          sizes="(min-width:641px) 25vw, 50vw" alt="${hit.imageAltText ? hit.imageAltText : hit.imageCaption ? hit.imageCaption : hit.imageTitle}"
    //                          class="calendar__event-image"/>
    //                 </a>`;
    //
    //
    //                 // if (eventDate > now) {
    //                     return html`
    //                         <div class="calendar__row" data-date="${formatUTCDateYmd(hit.start_date_timestamp, "UTC")}">
    //                             <div class="calendar__row-left">
    //                                 <h2 class="calendar__date-wrapper">
    //                                     ${hit.allDay ? rowDate : hit.multiDay ? multiDayRow : rowDate }
    //                                     ${hit.allDay ? rowWk : hit.multiDay ? multiDayWk : rowWk }
    //                                 </h2>
    //                             </div>
    //                             <div class="calendar__row-right">
    //                                 <div id="calendar__event" class="calendar__event" data-event-index="0">
    //                                     <div class="calendar__event__meta">
    //                                         <div class="calendar__event__meta-wrapper">
    //                                             <div class="calendar__event__meta-title">
    //                                                 <p>
    //                                                     <a href="${hit.siteUrl + hit.uri || '#'}"
    //                                                        ${hit.externalUrl === true ? html`target="_blank"` : ''}>
    //                                                         ${canceled}${components.Highlight({attribute: 'title', hit})}
    //                                                     </a>
    //                                                 </p>
    //                                             </div>
    //                                             ${hit.allDay ? '' : hit.multiDay ? timeDuration : timeDuration}
    //                                             ${hit.isRepeating && isYGreater ? rowY : ''}
    //                                         </div>
    //                                         <!-- image goes here -->
    //                                         ${hit.imageSrc ? image : ''}
    //                                     </div>
    //                                 </div>
    //                             </div>
    //                         </div>
    //                     `;
    //                 // } else {
    //                 //     return null;
    //                 // }
    //             },
    //             empty: (results, {html}) => {
    //                 console.log(results);
    //                 console.log(results.nbHits);
    //                 // console.log(results.hits|length === 0 ? 'true' : 'false');
    //                 // const eventDate = new Date(hit.start_date_timestamp * 1000);
    //                 // let now = Date.now();
    //                 // console.log(results);
    //                 // console.log(results.hits.length);
    //                 // console.log(document.querySelector('.event-search-results__wrapper'));
    //                 // document.querySelector('.event-search-results__wrapper').classList.add('empty');
    //
    //                 // console.log(eventDate < now);
    //                 if (results.query === '') return null;
    //                 return html`<h4>No events found for "${results.query}"</h4>`;
    //
    //             },
    //         },
    //     }),
    //     stats({
    //         container: '#event-stats',
    //         templates: {
    //             text: ({
    //                        nbHits,
    //                        hasNoResults,
    //                        hasOneResult,
    //                        processingTimeMS,
    //                        query,
    //                    }) => {
    //                 let statsText = '';
    //                 if (hasNoResults) {
    //                     statsText = 'no results';
    //                 } else if (hasOneResult) {
    //                     statsText = '1 result found';
    //                 } else {
    //                     statsText = `${nbHits.toLocaleString()} results`;
    //                 }
    //                 return query !== ''
    //                     ? `Found ${statsText}.`
    //                     : ' ';
    //             },
    //         },
    //         cssClasses: {
    //             root: 'results__stats',
    //         },
    //     }),
    //     eventsRenderWidget,
    //     pagination({
    //         container: '#event-pagination',
    //         cssClasses: {
    //             root: 'query__pagination',
    //             list: 'page-nav',
    //             selectedItem: 'current-page',
    //         },
    //         // templates: {
    //         //     first() {
    //         //         return `<svg class="svg-inline--fa" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Pro 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2025 Fonticons, Inc.--><path d="M47 239c-9.4 9.4-9.4 24.6 0 33.9L239 465c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9L97.9 256 273 81c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0L47 239zM431 47L239 239c-9.4 9.4-9.4 24.6 0 33.9L431 465c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-175-175L465 81c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0z"/></svg>`
    //         //     },
    //         //     previous() {
    //         //         return `<svg class="svg-inline--fa" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--!Font Awesome Pro 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2025 Fonticons, Inc.--><path d="M15 239c-9.4 9.4-9.4 24.6 0 33.9L207 465c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9L65.9 256 241 81c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0L15 239z"/></svg>`
    //         //     },
    //         //     next() {
    //         //         return `<svg class="svg-inline--fa" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--!Font Awesome Pro 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2025 Fonticons, Inc.--><path d="M305 239c9.4 9.4 9.4 24.6 0 33.9L113 465c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l175-175L79 81c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0L305 239z"/></svg>`
    //         //     },
    //         //     last() {
    //         //         return `<svg class="svg-inline--fa" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Pro 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2025 Fonticons, Inc.--><path d="M465 239c9.4 9.4 9.4 24.6 0 33.9L273 465c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l175-175L239 81c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0L465 239zM81 47L273 239c9.4 9.4 9.4 24.6 0 33.9L81 465c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l175-175L47 81c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0z"/></svg>`
    //         //     },
    //         //
    //         // },
    //     }),
    //     customPagination({
    //         container: document.querySelector('#event-pagination'),
    //     })
    // ]),
]);

eventsSearch.addWidgets([
    virtualSearchBox({}),
    configure({
        hitsPerPage: 5
    }),
    hits({
        container: '#event-hits',
        templates: {
            item(hit, {html, components}) {
                const nowTimestamp = Date.now();
                const isFutureYear = formatUTCDateY(hit.start_date_timestamp, 'UTC') > formatUTCDateNOWY(nowTimestamp, 'UTC');
                const multiDayWk =
                    formatDateWithMM(hit.start_date_timestamp, 'UTC') === formatDateWithMM(hit.end_date_timestamp, 'UTC')
                        ? html`<span class="calendar__row-week">${formatDateWithDay(hit.start_date_timestamp, 'UTC')} –${formatDateWithDay(hit.end_date_timestamp, 'UTC')}</span>`
                        : '';
                const multiDayRow = html`<span class="calendar__row-date">${formatDateWithMMDD(hit.start_date_timestamp, 'UTC')}–${formatDateWithMM(hit.start_date_timestamp, 'UTC') !== formatDateWithMM(hit.end_date_timestamp, 'UTC') ? formatDateWithMMDD(hit.end_date_timestamp, 'UTC') : formatDateWithDD(hit.end_date_timestamp, 'UTC')}</span>`;
                const rowDate = html`<span class="calendar__row-date">${formatDateWithMMDD(hit.start_date_timestamp, 'UTC')}</span>`;
                const rowWk = html`<span class="calendar__row-week">${formatDateWithDay(hit.start_date_timestamp, 'UTC')}</span>`;
                const rowY = html`<p class="calendar__event__meta-year">${formatUTCDateY(hit.start_date_timestamp, 'UTC')}</p>`;
                const timeDuration = html`<p class="calendar__event__meta-time">${formatUTCDateTime(hit.start_date_timestamp, 'UTC').replace(/\s+[AP]M/i, '')} –${formatUTCDateTime(hit.end_date_timestamp, 'UTC')}</p>`;
                const canceled = hit.isCanceled === true ? html`CANCELLED: ` : '';
                const image = html`<a class="calendar__event-hero-img" href="${hit.siteUrl + hit.uri || '#'}" ${hit.externalUrl === true ? html`target="_blank"` : ''}>
                    <img src="${hit.imageSrc}" srcset="${hit.imageSrcset}" sizes="(min-width:641px) 25vw, 50vw" alt="${hit.imageAltText ? hit.imageAltText : hit.imageCaption ? hit.imageCaption : hit.imageTitle}" class="calendar__event-image"/>
                </a>`;
                const andoverAddToCalendar =
                    html `
                        <div title="Add to Calendar" class="addeventatc event-addtocalendar event-list"
                             data-dropdown-x="right">
                            <i class="fal fa-calendar-plus" aria-hidden="true"></i>
                            <span class="start">${formatUTCDateCalendar(hit.start_date_timestamp, 'UTC')}</span>
                            <span class="end">${formatUTCDateCalendar(hit.end_date_timestamp, 'UTC')}</span>
                            <span class="timezone">America/New_York</span>
                            <span class="organizer">Andover</span>
                            <span
                                class="title">${hit.isCanceled === true ? 'CANCELLED: ' : ''}${hit.title}</span>
                            <span class="description">${hit.content}</span>
                            <span class="location">${hit.location != '' ? hit.location : ''}</span>
                        </div>
                    `;
                const showAndoverAddtoCalendar = hit.isCanceled === false && utcTimestamp <= hit.end_date_timestamp ? andoverAddToCalendar : '';

                const andoverEvents =
                    html`
                        <div class="calendar--andover calendar__row" data-date="${formatUTCDateYmd(hit.start_date_timestamp, 'UTC')}">
                            <div class="calendar__row-left">
                                <h2 class="calendar__date-wrapper">
                                    ${hit.allDay ? rowDate : hit.multiDay ? multiDayRow : rowDate}
                                    ${hit.allDay ? rowWk : hit.multiDay ? multiDayWk : rowWk}
                                </h2>
                            </div>
                            <div class="calendar__row-right">
                                <div id="calendar__event" class="calendar__event" data-event-index="0">
                                    <div class="calendar__event__meta">
                                        <div class="calendar__event__meta-wrapper">
                                            <div class="calendar__event__meta-title">
                                                <p>
                                                    <a href="${hit.registerUrl ? hit.registerUrl : hit.siteUrl + hit.uri}" ${hit.registerUrl != '' ? html`target="_blank"` : ''}>
                                                        ${canceled}${components.Highlight({attribute: 'title', hit})}
                                                    </a> ${showAndoverAddtoCalendar}
                                                </p>
                                            </div>
                                            ${hit.allDay ? '' : timeDuration}
                                            ${hit.isRepeating && isFutureYear ? rowY : ''}
                                        </div>
                                        ${hit.imageSrc ? image : ''}
                                    </div>
                                </div>
                            </div>
                        </div>
                    `;

                // ATHLETICS EVENTS
                const home = hit.homeAway === 'home'
                    ? html`<div class="location-home"><span class="location-txt">H</span></div>`
                    : '';
                const away = hit.homeAway === 'away'
                    ? html`<div class="location-away"><span class="location-txt">A</span></div>`
                    : '';
                // // console.log(hit.location);
                const loc = Array.isArray(hit.location) ? hit.location[0] : hit.location;
                const mapPinLocationUrl = loc?.url ?? '';
                const mapPinLocationTitle = loc?.title ?? '';
                const mapPinHTML = html`<a href="${mapPinLocationUrl}" target="_blank" class="event-location"><i class="fal fa-location-dot"></i><span class="visually-hidden">Map to ${mapPinLocationTitle}</span></a>`;
                // console.log('location raw:', hit.location);
                // console.log('location keys:', hit.location ? Object.keys(hit.location) : null);
                // console.log('location json:', JSON.stringify(hit.location, null, 2));
                const athleticsMapPin = mapPinLocationUrl ? mapPinHTML : '';
                    // ? html`<a href="${hit.location?.url}" target="_blank" class="event-location"><i class="fal fa-location-dot"></i><span class="visually-hidden">Map to ${hit.location?.title}</span></a>`
                    // : '';
                const localLivestream = hit.livestream !== ''
                    ? html`<a href="${hit.livestream}" className="event-livestream"><i className="fal fa-tv"></i></a>`
                    : '';
                const exLivestream = hit.livestreamExLocation !== ''
                    ? html`<a href="${hit.livestreamExLocation}" target="_blank" className="event-livestream"><i className="fal fa-tv"></i></a>`
                    : '';
                const showLocalTv = utcTimestamp <= hit.end_date_timestamp ? localLivestream : '';
                const showXTv = utcTimestamp <= hit.end_date_timestamp ? exLivestream : '';
                const showTv = utcTimestamp <= hit.end_date_timestamp && hit.isCanceled === '' && hit.livestream !== ''
                    ? showLocalTv
                    : utcTimestamp <= hit.end_date_timestamp && hit.isCanceled === '' && hit.livestreamExLocation !== ''
                        ? showXTv
                        : '';
                const athleticsAddToCalendar =
                    html`<div title="Add to Calendar" class="addeventatc event-addtocalendar" data-dropdown-y="up" data-dropdown-x="right">
                            <i class="fal fa-calendar-plus"></i>
                            <span class="start">${formatUTCDateCalendar(hit.start_date_timestamp, 'UTC')}</span>
                            <span class="end">${formatUTCDateCalendar(hit.end_date_timestamp, 'UTC')}</span>
                            <span class="timezone">America/New_York</span>
                            <span class="title">${hit.title} ${hit.homeAway === 'home' ? 'vs' : 'at'} ${hit.opponent}</span>
                            <span class="description">${hit.title} ${hit.homeAway === 'home' ? 'vs' : 'at'} ${hit.opponent}</span>
                            <span class="location">${hit.homeAway === 'home' ? 'Andover' : hit.location?.title}</span>
                        </div>`;
                const showAthleticsAddtoCalendar = hit.isCanceled === '' && utcTimestamp <= hit.end_date_timestamp ? athleticsAddToCalendar : '';

                const athleticsEvents =
                    html`
                            <section class="calendar--athletics event__wrapper${hit.isCanceled === 'Cancelled' ? ' event-cancelled' : ''}" data-date="${formatUTCDateYmd(hit.start_date_timestamp, "UTC")}">
                                <div class="event-head date-time">
                                    <div class="event-date">
                                        ${hit.allDay ? rowDate : hit.multiDay ? multiDayRow : rowDate }
                                    </div>
                                    <div class="event-time">
                                        ${formatUTCDateTime(hit.start_date_timestamp, "UTC")}
                                    </div>
                                </div>
                                <div class="event-body">
                                    <div class="team team-home">
                                        ${home}
                                        <a href="${hit.siteUrl}teams/${hit.teamSlug}/schedule" class="event-opponent">${components.Highlight({attribute: 'title', hit})}</a>
                                        ${hit.andoverScore !== '' ? html`<p class="event-result">${hit.andoverScore}</p>` : ''}
                                    </div>
                                    <div class="team team-away">
                                        ${away}
                                        <p class="event-opponent">${components.Highlight({attribute: 'opponent', hit})}</p>${athleticsMapPin}
                                        ${hit.opponentScore !== '' ? html`<p class="event-result">${hit.opponentScore}</p>` : ''}
                                    </div>
                                </div>
                                <div class="event-footer">
                                    ${hit.isCanceled !== '' ? html`<p class="event-change">${hit.isCanceled}</p>` : ''}
                                    ${showTv}
                                    ${showAthleticsAddtoCalendar}
                                </div>
                            </section>
                        `;

                // html`
                //             <section class="event__wrapper${hit.isCanceled === 'Cancelled' ? ' event-cancelled' : ''}" data-date="${formatUTCDateYmd(hit.start_date_timestamp, "UTC")}">
                //                 <div class="event-head date-time">
                //                     <div class="event-date">
                //                         ${hit.allDay ? rowDate : hit.multiDay ? multiDayRow : rowDate }
                //                     </div>
                //                     <div class="event-time">
                //                         ${formatUTCDateTime(hit.start_date_timestamp, "UTC")}
                //                     </div>
                //                 </div>
                //                 <div class="event-body">
                //                     <div class="team team-home">
                //                         ${home}
                //                         <a href="${hit.siteUrl}/teams/${hit.teamSlug}/schedule" class="event-opponent">${components.Highlight({attribute: 'title', hit})}</a>
                //                         ${hit.andoverScore !== '' ? html`<p class="event-result">${hit.andoverScore}</p>` : ''}
                //                     </div>
                //                     <div class="team team-away">
                //                         ${away}
                //                         <p class="event-opponent">${components.Highlight({attribute: 'opponent', hit})}</p>${mapPin}
                //                         ${hit.opponentScore !== '' ? html`<p class="event-result">${hit.opponentScore}</p>` : ''}
                //                     </div>
                //                 </div>
                //                 <div class="event-footer">
                //                     ${hit.isCanceled !== '' ? html`<p class="event-change">${hit.isCanceled}</p>` : ''}
                //                     ${showTv}
                //                     ${showAddtoCalendar}
                //                 </div>
                //             </section>
                //         `;

                return html`
                    ${hit.site === 'andover' ? andoverEvents : athleticsEvents }
                `;
            },
            empty(results, {html}) {
                if (results.query === '') return null;
                return html`<h4>No events found for "${results.query}"</h4>`;
            },
        },
    }),
    stats({
        container: '#event-stats',
        templates: {
            text: ({
                       nbHits,
                       hasNoResults,
                       hasOneResult,
                       query,
                   }) => {
                let statsText = '';
                if (hasNoResults) {
                    statsText = 'no results';
                } else if (hasOneResult) {
                    statsText = '1 result found';
                } else {
                    statsText = `${nbHits.toLocaleString()} results`;
                }
                return query !== ''
                    ? `Found ${statsText}.`
                    : ' ';
            },
        },
        cssClasses: {
            root: 'results__stats',
        },
    }),
    pagination({
        container: '#event-pagination',
        cssClasses: {
            root: 'query__pagination',
            list: 'page-nav',
            selectedItem: 'current-page',
        },
    }),
    eventsRenderWidget,
    index({indexName: eventAthleticsIndexName}).addWidgets([
        // Keep the athletics index participating in InstantSearch requests so union can merge results.
        configure({})
    ]),
    customPagination({
        container: document.querySelector('#event-pagination'),
    })
]);

const searchFormInput = document.querySelector('.ais-SearchBox-input');
const searchSuggestions = document.querySelector('#search-suggestions');

console.log(params);
console.log(initialQuery);
// console.log(initialOQuery);

const setMainUiState = (query) => {
    mainSearch.setUiState({
        [indexName]: {
            query,
            page: 1,
        },
        [athleticsIndexName]: {
            query,
            page: 1,
        },
    });
};
const setEventUiState = (query) => {
    // eventsSearch is a separate InstantSearch instance (for event results only).
    // It does not own the visible search input, so we must keep its query in sync manually.
    eventsSearch.setUiState({
        [eventIndexName]: {
            query,
            page: 1,
        },
        [eventAthleticsIndexName]: {
            query,
            page: 1,
        },
    });
};
const syncEventQuery = (query) => {
    // Central helper so every caller uses one path for event-query updates.
    setEventUiState(query);
};

if (initialQuery) {
    searchSuggestions.style.display = 'none';
}

let lastSyncedQuery = initialQuery;
let lastUrlSyncedQuery = initialQuery;
let lastUrlSyncedMainPage = initialMainPage;
let lastUrlSyncedEventsPage = initialEventsPage;
// URL sync behavior:
// - This page has 2 InstantSearch instances (main + events), each with its own pagination state.
// - We store state in URL as:
//   q  = query text
//   p  = main results page
//   ep = events results page
// - events pagination can change without a main render, so URL sync must run from both render hooks.
// - lastUrlSynced* guards prevent redundant replaceState churn.
const syncUrlFromSearchState = () => {
    const query = mainSearch.getUiState()[indexName]?.query ?? '';
    const mainPage = mainSearch.getUiState()[indexName]?.page ?? 1;
    const eventsPage = eventsSearch.getUiState()[eventIndexName]?.page ?? 1;

    if (
        query !== lastUrlSyncedQuery
        || mainPage !== lastUrlSyncedMainPage
        || eventsPage !== lastUrlSyncedEventsPage
    ) {
        lastUrlSyncedQuery = query;
        lastUrlSyncedMainPage = mainPage;
        lastUrlSyncedEventsPage = eventsPage;
        const nextParams = new URLSearchParams(window.location.search);
        nextParams.delete('q');
        nextParams.delete('p');
        nextParams.delete('ep');
        for (const key of [...nextParams.keys()]) {
            if (key.endsWith('[query]')) {
                nextParams.delete(key);
            }
        }
        if (query) {
            nextParams.set('q', query);
            if (mainPage > 1) {
                nextParams.set('p', String(mainPage));
            }
            if (eventsPage > 1) {
                nextParams.set('ep', String(eventsPage));
            }
        }
        const nextQuery = nextParams.toString();
        const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`;
        window.history.replaceState({}, '', nextUrl);
    }
};

mainSearch.on('render', () => {
    // Query bridge: copy the current mainSearch query into eventsSearch.
    // Why this exists:
    // - mainSearch owns the search box + content union results.
    // - eventsSearch renders event union results in a separate instance.
    // Without this bridge, eventsSearch query can lag or stay undefined.
    // lastSyncedQuery prevents duplicate event searches on repeated renders.
    const query = mainSearch.getUiState()[indexName]?.query ?? '';
    if (query !== lastSyncedQuery) {
        lastSyncedQuery = query;
        setEventUiState(query);
    }
    syncUrlFromSearchState();

    // Keep suggestions visibility derived from current query state.
    if (searchSuggestions) {
        searchSuggestions.style.display = query ? 'none' : 'block';
    }
});

eventsSearch.on('render', () => {
    syncUrlFromSearchState();
});


mainSearch.start();
eventsSearch.start();

// if (initialQuery) {
//     setMainUiState(initialQuery);
//     eventsSearch.helper.setQuery(initialQuery).search();
//
//     searchSuggestions.style.display = 'none';
// }


// SEARCH FORM HIDE SEARCH SUGGESTIONS ON ENTER KEY
// let searchQuery = '';
// searchFormInput.addEventListener('keydown', (e) => {
//     if (e.key === 'Enter') {
//         searchQuery = e.target.value;
//         console.log(searchQuery);
//         setEventUiState(searchQuery);
//         eventsSearch.helper.setQuery(searchQuery).search();
//         if (searchQuery) {
//             searchSuggestions.style.display = 'none';
//         }
//     }
// });


document.addEventListener('click', (event) => {
    const resetButton = event.target.closest('.ais-SearchBox-reset');
    if (!resetButton) {
        return;
    }

        console.log('Before reset:', mainSearch.getUiState());
        if (searchSuggestions) {
            searchSuggestions.style.display = 'block';
        }
        if (searchFormInput) {
            searchFormInput.value = '';
        }

        setMainUiState('');
        syncEventQuery('');

        mainSearch.helper.setQuery('').search();
        // Keep URL in sync when clearing search from the reset button.
        const nextParams = new URLSearchParams(window.location.search);
        nextParams.delete('q');
        nextParams.delete('p');
        nextParams.delete('ep');
        for (const key of [...nextParams.keys()]) {
            if (key.endsWith('[query]')) {
                nextParams.delete(key);
            }
        }
        const nextQuery = nextParams.toString();
        const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`;
        window.history.replaceState({}, '', nextUrl);

        console.log('After reset:', mainSearch.getUiState());
        // console.log('SearchBox query:', mainSearch.renderState[indexName].searchBox?.query);
});



function formatUTCDate(timestamp, timezone = 'UTC') {
    const date = new Date(timestamp * 1000);
    const options = {
        weekday: 'short',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: timezone // Use 'America/New_York' for EDT
    };
    return date.toLocaleString('en-US', options).replace(/, (\d+),/, ', $1 at');
}
function formatUTCDateYmd(timestamp, timezone = 'UTC') {
    const date = new Date(timestamp * 1000);
    const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone: timezone // Use 'America/New_York' for EDT
    };
    const [month, day, year] = date.toLocaleString('en-US', options).split(/[\/]/);
    return `${year}-${month}-${day}`;
}
function formatUTCDateY(timestamp, timezone = 'UTC') {
    const date = new Date(timestamp * 1000);
    const options = {
        year: 'numeric',
        timeZone: timezone // Use 'America/New_York' for EDT
    };
    const [year] = date.toLocaleString('en-US', options).split(/[\/]/);
    return `${year}`;
}
function formatUTCDateNOWY(timestamp, timezone = 'UTC') {
    const date = new Date(timestamp);
    const options = {
        year: 'numeric',
        timeZone: timezone // Use 'America/New_York' for EDT
    };
    const [year] = date.toLocaleString('en-US', options).split(/[\/]/);
    return `${year}`;
}
function formatDateWithMMDD(timestamp, timezone = 'UTC') {
    const date = new Date(timestamp * 1000);
    const options = {
        month: '2-digit',
        day: '2-digit',
        timeZone: timezone
    };
    const [month, day] = date.toLocaleString('en-US', options).split(/[\/, ]+/);
    return `${month}.${day}`;
}
function formatDateWithMM(timestamp, timezone = 'UTC') {
    const date = new Date(timestamp * 1000);
    const options = {
        month: '2-digit',
        timeZone: timezone
    };
    const [month] = date.toLocaleString('en-US', options).split(/[\/]+/);
    return `${month}`;
}
function formatDateWithDD(timestamp, timezone = 'UTC') {
    const date = new Date(timestamp * 1000);
    const options = {
        day: '2-digit',
        timeZone: timezone
    };
    const [day] = date.toLocaleString('en-US', options).split(/[\/]+/);
    return `${day}`;
}
function formatDateWithDay(timestamp, timezone = 'UTC') {
    const date = new Date(timestamp * 1000);
    const options = {
        weekday: 'short',
        timeZone: timezone
    };
    const [day] = date.toLocaleString('en-US', options).split(/[\/]+/);
    return `${day}`;
}
function formatUTCDateTime(timestamp, timezone = 'UTC') {
    const date = new Date(timestamp * 1000);
    const options = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: timezone // Use 'America/New_York' for EDT
    };
    return date.toLocaleString('en-US', options);
}

function formatUTCDateCalendar(timestamp, timezone = 'UTC') {
    const date = new Date(timestamp * 1000); // Convert seconds to milliseconds
    const options = {
        month: 'numeric', // m (no leading zeros)
        day: 'numeric', // d (no leading zeros)
        year: 'numeric', // Y (full year)
        hour: 'numeric', // g (hour without leading zeros)
        minute: '2-digit', // i (minutes with leading zeros)
        hour12: true, // Enables AM/PM
        timeZone: timezone
    };
    const formatted = date.toLocaleString('en-US', options); // e.g., "7/23/2025, 11:00 PM"
    // Convert to "m/d/Y g:i A"
    return formatted.replace(/,/, '').replace(/([ap])m/i, '$1M'); // Remove comma, ensure uppercase AM/PM
}
